import {
  ChangeEvent,
  ChangeEventHandler,
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import Checkmark from 'assets/list-checkmark.svg';
import { Visible } from 'coreOld/components';
import { getDisplayName } from 'coreOld/utils';

interface IProps {
  value?: string;
  name?: string;
  defaultChecked?: boolean;
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  id?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  title?: string;
  style?: CSSProperties;
  isLoading?: boolean;
}

interface CheckboxContainerProps {
  disabled?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
  htmlFor?: string;
}

const CheckboxContainer = styled.label<CheckboxContainerProps>`
  display: flex;
  font-size: 14px;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: 0.5rem;

  ${({ style }) => ({ ...style })}
`;

interface LabelProps {
  children?: ReactNode;
}

const Label = styled.span<LabelProps>`
  margin-left: 10px;
`;

interface IconProps {
  src?: string;
  alt?: string;
}

const Icon = styled.img<IconProps>`
  width: 12px;
  height: 12px;
`;

interface HiddenCheckboxProps {
  name?: string;
  title?: string;
  ref?: any;
  type?: string;
  id?: string;
  value?: any;
  defaultChecked?: boolean;
  checked?: boolean;
  onChange?: any;
  disabled?: boolean;
}

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })<HiddenCheckboxProps>`
  border: 0;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  visibility: hidden;
`;

interface StyledCheckboxProps {
  checked?: boolean;
  error?: boolean;
  children?: ReactNode;
}

const StyledCheckbox = styled.div<StyledCheckboxProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.palette.backgrounds.main};
  transition: all 150ms;
  border: ${({ theme }) => `1px solid ${theme.palette.checkMark}`};

  ${Icon} {
    visibility: ${(props) => (props.checked ? 'visible' : 'hidden')};
  }
`;

const Checkbox = forwardRef(
  (
    {
      value,
      defaultChecked,
      checked,
      disabled,
      label,
      onChange,
      id,
      title,
      style,
      name,
      isLoading = false,
    }: IProps,
    ref
  ) => {
    const [isChecked, setIsChecked] = useState(defaultChecked || checked);

    useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      setIsChecked(event.target.checked);

      onChange && onChange(event);
    };

    return (
      <CheckboxContainer disabled={disabled} htmlFor={id} style={style}>
        <HiddenCheckbox
          name={name}
          title={title}
          ref={ref}
          type="checkbox"
          id={id}
          value={value}
          defaultChecked={defaultChecked}
          checked={isChecked}
          onChange={handleOnChange}
          disabled={disabled || isLoading}
        />

        <StyledCheckbox checked={defaultChecked || isChecked}>
          <Icon src={Checkmark} />
        </StyledCheckbox>

        <Visible when={Boolean(label)}>
          <Label>{label}</Label>
        </Visible>
      </CheckboxContainer>
    );
  }
);

Checkbox.displayName = `Checkbox(${getDisplayName(Checkbox)})`;

export default Checkbox;
